import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { BsStripe } from "react-icons/bs";
import { IoIosCloseCircleOutline } from 'react-icons/io';
import PropTypes from 'prop-types';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import { Switch } from '@mui/material';
import { formatDate, today } from '../../data/constants';
import { Link } from 'react-router-dom';
import { FaEye } from "react-icons/fa";
import { CSVLink } from 'react-csv';
import { SiWise } from 'react-icons/si';
import { FaPaypal } from 'react-icons/fa';
import { showToast } from "../../Components/utils/ShowToast";
import Loader from '../../Components/utils/Loader';


// Indexed by tab position — keep in sync with the <Tab> order below.
const TAB_REQUEST_TYPES = [
    'pendingPayments',
    'completedPayments',
    'pendingRefunds',
    'completedRefunds',
    'pendingWisePayments',
    'completedWisePayments',
    'pendingPaypalPayments',
    'completedPaypalPayments',
];

const AdminPayments = () => {

    const [stripePublishableKey, setStripePublishableKey] = useState('');
    const [stripeSecretKey, setStripeSecretKey] = useState('');
    const [stripeWebhookSecret, setStripeWebhookSecret] = useState('');
    const [showAddStripeModel, setShowAddStripeModel] = useState(false);
    const token = localStorage.getItem("adminToken");
    const [withdrawalRequests, setWithdrawalRequests] = useState([]);
    const [paymentDate, setPaymentDate] = useState('');
    const [comment, setComment] = useState('');
    const [showMarkPaidModel, setShowMarkPaidModel] = useState(null);
    const [showReleaseModel, setShowReleaseModel] = useState(null);
    const [releaseLoading, setReleaseLoading] = useState(false);
    const [showRejectModel, setShowRejectModel] = useState(null);
    const [rejectReason, setRejectReason] = useState('');

    const [selectedRequests, setSelectedRequests] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [value, setValue] = React.useState(0);
    const [paymentMethodToggles, setPaymentMethodToggles] = useState({ stripe: true, wise: true, paypal: true });

    const handleChange = (event, newValue) => {
        setValue(newValue);
        setSelectedRequests([]);
    };

    const handleTogglePaymentMethod = async (method, enabled) => {
        try {
            const res = await axios.post(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/restrictions/payment-method/toggle`,
                { method, enabled },
                { headers: { Authorization: `Admin ${token}` } }
            );
            if (res.data.success) {
                setPaymentMethodToggles(res.data.paymentMethods);
                showToast(`${method.charAt(0).toUpperCase() + method.slice(1)} ${enabled ? 'enabled' : 'disabled'} successfully!`, 'success');
            }
        } catch {
            showToast('Failed to update payment method', 'error');
        }
    };

    const fetchWithdrawalRequests = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await axios.get(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/payments/withdrawal-requests?type=${TAB_REQUEST_TYPES[value]}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success) {
                setWithdrawalRequests(response.data.withdrawalRequests);
            }
        } catch (error) {
            console.error("Error fetching withdrawal requests:", error);
            showToast('Failed to load withdrawal requests', 'error');
        }
        finally {
            setIsLoading(false);
        }
    }, [token, value]);

    /**
     * Each request in a batch is settled independently on the server, so a batch
     * can come back partly applied. Resync from the server instead of assuming
     * every selected row succeeded — otherwise a retry after a partial failure
     * would re-send payments that already went out.
     */
    const applyBatchResult = useCallback((data, successVerb) => {
        const processed = data?.processed?.length ?? 0;
        const failed = data?.failed ?? [];

        if (processed > 0)
            showToast(`${processed} payment${processed > 1 ? 's' : ''} ${successVerb}!`, 'success');

        if (failed.length > 0)
            showToast(`${failed.length} could not be processed — ${failed[0].message}`, 'error');

        if (processed === 0 && failed.length === 0)
            showToast('Nothing to process', 'error');

        setSelectedRequests([]);
        fetchWithdrawalRequests();
    }, [fetchWithdrawalRequests]);

    useEffect(() => {

        const fetchStripeKey = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/settings/admin/keys`, { headers: { Authorization: `Admin ${token}` } });
                if (response.data.success) {
                    setStripePublishableKey(response.data.stripePublishableKey || '')
                    setStripeSecretKey(response.data.stripeSecretKey || '')
                    setStripeWebhookSecret(response.data.stripeWebhookSecret || '')
                }
            } catch (error) {
                console.error("Error fetching Stripe keys:", error);
            }
        };

        const fetchPaymentMethodToggles = async () => {
            try {
                const res = await axios.get(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/restrictions`, { headers: { Authorization: `Admin ${token}` } });
                if (res.data.success && res.data.restriction?.paymentMethods) {
                    setPaymentMethodToggles(res.data.restriction.paymentMethods);
                }
            } catch (error) {
                console.error("Error fetching payment method toggles:", error);
            }
        };

        fetchWithdrawalRequests();
        fetchStripeKey();
        fetchPaymentMethodToggles();

    }, [token, fetchWithdrawalRequests]);

    const handleStripeDetailsSubmit = async (e) => {
        e.preventDefault();

        if (!stripePublishableKey || !stripeSecretKey) {
            showToast('Please fill in both fields', 'error');
            return;
        }

        try {
            setIsLoading(true);
            const response = await axios.post(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/settings/admin/stripe_keys`,
                { stripePublishableKey, stripeSecretKey, stripeWebhookSecret },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (response.data.success) {
                showToast('Stripe account details saved successfully!', 'success');
                setShowAddStripeModel(false);
            }

        } catch (error) {
            showToast('Failed to save Stripe account details', 'error');
            console.error(error);
        }
        finally{
            setIsLoading(false);
        }
    };

    const handleSelectRequest = (requestId) => {
        setSelectedRequests(prev =>
            prev.includes(requestId)
                ? prev.filter(id => id !== requestId)
                : [...prev, requestId]
        );
    };

    const handleSelectAll = () => {
        if (selectedRequests.length === withdrawalRequests.length)
            setSelectedRequests([]);
        else {
            const allRequestIds = withdrawalRequests.map(request => request._id);
            setSelectedRequests(allRequestIds);
        }
    };

    const markPaidManually = async () => {

        if (!paymentDate || !comment) {
            showToast('Please provide payment date and comment', 'error');
            return;
        }

        try {
            setIsLoading(true);
            const response = await axios.put(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/payments/mark-paid`, {
                requestIds: selectedRequests,
                paidOn: paymentDate,
                comment,
                isRelease: showMarkPaidModel === "Release" ? true : false
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setShowMarkPaidModel(null);
            applyBatchResult(response.data, 'marked as Paid');

        } catch (error) {
            console.error(error);
            showToast(error.response?.data?.message || 'Something went wrong!', 'error');
            setSelectedRequests([]);
            fetchWithdrawalRequests();
        }
        finally{
            setIsLoading(false);
        }
    }

    const releasePayments = async () => {

        setReleaseLoading(true);

        try {
            setIsLoading(true);
            const response = await axios.put(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/payments/release-payment`, {
                requestIds: selectedRequests
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setShowReleaseModel(null);
            applyBatchResult(response.data, 'released successfully');
        } catch (error) {
            console.log(error);
            showToast(error.response?.data?.message || 'Something went wrong!', 'error');
            // Some transfers in the batch may already have been sent — clear the
            // selection and resync so a retry cannot re-send them.
            setSelectedRequests([]);
            fetchWithdrawalRequests();
        }
        finally{
            setReleaseLoading(false);
            setIsLoading(false);
        }
    }

    const refundPayments = async () => {

        setReleaseLoading(true);

        try {
            setIsLoading(true);
            const response = await axios.put(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/payments/refund-payment`, {
                requestIds: selectedRequests
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setShowReleaseModel(null);
            applyBatchResult(response.data, 'refunded successfully');
        } catch (error) {
            console.log(error);
            showToast(error.response?.data?.message || 'Something went wrong!', 'error');
            setSelectedRequests([]);
            fetchWithdrawalRequests();
        }
        finally{
            setReleaseLoading(false);
            setIsLoading(false);
        }
    }

    const moveToPending = async (type) => {

        try {
            setIsLoading(true);
            const response = await axios.put(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/payments/move-to-pending`, { requestIds: selectedRequests, isRefund: type === "Refund" ? true : false }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            applyBatchResult(response.data, 'moved to the Pending tab');

        } catch (error) {
            console.error(error);
            showToast(error.response?.data?.message || 'Something went wrong!', 'error');
            setSelectedRequests([]);
            fetchWithdrawalRequests();
        }
        finally{
            setIsLoading(false);
        }
    }

    const rejectRequests = async () => {
        if (!rejectReason.trim()) {
            showToast('Please give a reason — the seller is told why.', 'error');
            return;
        }

        try {
            setIsLoading(true);
            const response = await axios.put(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/payments/reject-request`,
                { requestIds: selectedRequests, reason: rejectReason.trim() },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setShowRejectModel(null);
            setRejectReason('');
            applyBatchResult(response.data, 'rejected — the held balance has been returned');
        } catch (error) {
            console.error(error);
            showToast(error.response?.data?.message || 'Something went wrong!', 'error');
            setSelectedRequests([]);
            fetchWithdrawalRequests();
        } finally {
            setIsLoading(false);
        }
    }

    const selectedTotals = () => withdrawalRequests
        .filter(req => selectedRequests.includes(req._id))
        .reduce((totals, req) => ({
            gross: totals.gross + req.amount,
            fee: totals.fee + (req.payoutFee || 0),
            net: totals.net + (req.payableAmount ?? req.amount)
        }), { gross: 0, fee: 0, net: 0 });

    return (
        <div className="adminPaymentsDiv">

            {isLoading && <Loader type="checkmate" />}

            <div className="adminPaymentsContent">

                <div className='adminPaymentMethodsDiv'>
                    <h2 className='secondaryHeading'><span>Payment</span> Methods</h2>
                    <div className="paymentMethods">
                        <div className="method"><BsStripe />Stripe</div>
                        <div className="methodRight">
                            <Switch checked={paymentMethodToggles.stripe} onChange={(e) => handleTogglePaymentMethod('stripe', e.target.checked)} color="primary" />
                            <button className="secondaryBtn" onClick={() => setShowAddStripeModel(true)}>Manage</button>
                        </div>
                    </div>
                    <div className="paymentMethods">
                        <div className="method"><SiWise />Wise</div>
                        <div className="methodRight">
                            <Switch checked={paymentMethodToggles.wise} onChange={(e) => handleTogglePaymentMethod('wise', e.target.checked)} color="primary" />
                        </div>
                    </div>
                    <div className="paymentMethods">
                        <div className="method"><FaPaypal />PayPal</div>
                        <div className="methodRight">
                            <Switch checked={paymentMethodToggles.paypal} onChange={(e) => handleTogglePaymentMethod('paypal', e.target.checked)} color="primary" />
                        </div>
                    </div>
                </div>

                <div className="paymentsDetails">
                    <Box sx={{ width: '100%' }}>
                        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                            <Tabs value={value} onChange={handleChange} aria-label="basic tabs example" variant="scrollable" scrollButtons="auto">
                                <Tab sx={{ fontSize: "12px" }} label="Stripe - Pending" {...a11yProps(0)} />
                                <Tab sx={{ fontSize: "12px" }} label="Stripe - Completed" {...a11yProps(1)} />
                                <Tab sx={{ fontSize: "12px" }} label="Refunds - Pending" {...a11yProps(2)} />
                                <Tab sx={{ fontSize: "12px" }} label="Refunds - Completed" {...a11yProps(3)} />
                                <Tab sx={{ fontSize: "12px" }} label="Wise - Pending" {...a11yProps(4)} />
                                <Tab sx={{ fontSize: "12px" }} label="Wise - Completed" {...a11yProps(5)} />
                                <Tab sx={{ fontSize: "12px" }} label="Paypal - Pending" {...a11yProps(6)} />
                                <Tab sx={{ fontSize: "12px" }} label="Paypal - Completed" {...a11yProps(7)} />
                            </Tabs>
                        </Box>
                        <CustomTabPanel value={value} index={0}>
                            <PendingPayments
                                requests={withdrawalRequests}
                                selectedRequests={selectedRequests}
                                handleSelectRequest={handleSelectRequest}
                                handleSelectAll={handleSelectAll}
                                setShowMarkPaidModel={setShowMarkPaidModel}
                                setShowReleaseModel={setShowReleaseModel}
                                setShowRejectModel={setShowRejectModel}
                                paymentMethod="Stripe"
                            />
                        </CustomTabPanel>
                        <CustomTabPanel value={value} index={1}>
                            <CompletedPayments
                                requests={withdrawalRequests}
                                selectedRequests={selectedRequests}
                                handleSelectRequest={handleSelectRequest}
                                handleSelectAll={handleSelectAll}
                                moveToPending={moveToPending}
                                paymentMethod="Stripe"
                            />
                        </CustomTabPanel>
                        <CustomTabPanel value={value} index={2}>
                            <PendingRefunds
                                requests={withdrawalRequests}
                                selectedRequests={selectedRequests}
                                handleSelectRequest={handleSelectRequest}
                                handleSelectAll={handleSelectAll}
                                setShowMarkPaidModel={setShowMarkPaidModel}
                                setShowReleaseModel={setShowReleaseModel}
                                setShowRejectModel={setShowRejectModel}
                            />
                        </CustomTabPanel>
                        <CustomTabPanel value={value} index={3}>
                            <CompletedRefunds
                                requests={withdrawalRequests}
                                selectedRequests={selectedRequests}
                                handleSelectRequest={handleSelectRequest}
                                handleSelectAll={handleSelectAll}
                                moveToPending={moveToPending}
                            />
                        </CustomTabPanel>
                        <CustomTabPanel value={value} index={4}>
                            <PendingPayments
                                requests={withdrawalRequests}
                                selectedRequests={selectedRequests}
                                handleSelectRequest={handleSelectRequest}
                                handleSelectAll={handleSelectAll}
                                setShowMarkPaidModel={setShowMarkPaidModel}
                                setShowReleaseModel={setShowReleaseModel}
                                setShowRejectModel={setShowRejectModel}
                                paymentMethod="Wise"
                            />
                        </CustomTabPanel>
                        <CustomTabPanel value={value} index={5}>
                            <CompletedPayments
                                requests={withdrawalRequests}
                                selectedRequests={selectedRequests}
                                handleSelectRequest={handleSelectRequest}
                                handleSelectAll={handleSelectAll}
                                moveToPending={moveToPending}
                                paymentMethod="Wise"
                            />
                        </CustomTabPanel>
                        <CustomTabPanel value={value} index={6}>
                            <PendingPayments
                                requests={withdrawalRequests}
                                selectedRequests={selectedRequests}
                                handleSelectRequest={handleSelectRequest}
                                handleSelectAll={handleSelectAll}
                                setShowMarkPaidModel={setShowMarkPaidModel}
                                setShowReleaseModel={setShowReleaseModel}
                                setShowRejectModel={setShowRejectModel}
                                paymentMethod="Paypal"
                            />
                        </CustomTabPanel>
                        <CustomTabPanel value={value} index={7}>
                            <CompletedPayments
                                requests={withdrawalRequests}
                                selectedRequests={selectedRequests}
                                handleSelectRequest={handleSelectRequest}
                                handleSelectAll={handleSelectAll}
                                moveToPending={moveToPending}
                                paymentMethod="Paypal"
                            />
                        </CustomTabPanel>
                    </Box>
                </div>

            </div>

            {showAddStripeModel && (
                <div className="popupDiv">

                    <div className="popupContent">

                        <div className='form'>
                            <h2 className="secondaryHeading">Add <span>Stripe</span> Details</h2>
                            <div className="horizontalLine"></div>
                            <div className='inputDiv'>
                                <label>Stripe Publishable Key <span>*</span></label>
                                <input
                                    type="text"
                                    value={stripePublishableKey}
                                    onChange={(e) => setStripePublishableKey(e.target.value)}
                                    placeholder="Enter your Stripe Publishable Key"
                                    className='inputField'
                                />
                            </div>
                            <div className='inputDiv'>
                                <label>Stripe Secret Key <span>*</span></label>
                                <input
                                    type="password"
                                    value={stripeSecretKey}
                                    onChange={(e) => setStripeSecretKey(e.target.value)}
                                    placeholder="Enter your Stripe Secret Key"
                                    className='inputField'
                                />
                            </div>
                            <div className='inputDiv'>
                                <label>Stripe Webhook Signing Secret</label>
                                <input
                                    type="password"
                                    value={stripeWebhookSecret}
                                    onChange={(e) => setStripeWebhookSecret(e.target.value)}
                                    placeholder="whsec_..."
                                    className='inputField'
                                />
                                <p className="stripeWebhookHint">
                                    Create an endpoint in your Stripe dashboard pointing at
                                    <code> {process.env.REACT_APP_Backend_Host_Name}/api/v1/payments/webhook </code>
                                    subscribed to <strong>payment_intent.succeeded</strong>,
                                    <strong> payment_intent.payment_failed</strong> and
                                    <strong> charge.refunded</strong>, then paste its signing secret here.
                                    Without it, orders cannot be recovered when a buyer's browser
                                    closes before checkout finishes.
                                </p>
                            </div>
                        </div>

                        <div className="buttonsDiv" style={{ marginTop: "20px" }}>
                            <button className="secondaryBtn" type="button" onClick={() => setShowAddStripeModel(false)}>Close</button>
                            <button type="submit" className='primaryBtn' onClick={handleStripeDetailsSubmit}>Save Stripe Details</button>
                        </div>

                    </div>

                    <div className="popupCloseBtn">
                        <IoIosCloseCircleOutline className="icon" onClick={() => setShowAddStripeModel(false)} />
                    </div>

                </div>
            )}

            {showMarkPaidModel && (
                <div className="popupDiv">

                    <div className="popupContent">

                        <div className="form">
                            <div className="inputDiv">
                                <label>Payment Date <span>*</span></label>
                                <input
                                    type="date"
                                    value={paymentDate}
                                    onChange={(e) => setPaymentDate(e.target.value)}
                                    className='inputField'
                                    max={today}
                                />
                            </div>
                            <div className="inputDiv">
                                <label>Comment <span>*</span></label>
                                <textarea
                                    value={comment}
                                    onChange={(e) => setComment(e.target.value)}
                                    placeholder="Enter a comment"
                                    className='inputField'
                                />
                            </div>
                        </div>

                        <div className="buttonsDiv" style={{ marginTop: "20px" }}>
                            <button className="secondaryBtn" onClick={() => setShowMarkPaidModel(null)}>Cancel</button>
                            <button className="primaryBtn" onClick={markPaidManually}>Mark Paid</button>
                        </div>

                    </div>

                    <div className="popupCloseBtn">
                        <IoIosCloseCircleOutline className="icon" onClick={() => setShowMarkPaidModel(null)} />
                    </div>

                </div>
            )}

            {showRejectModel && (
                <div className="popupDiv">
                    <div className="popupContent">
                        <div className="form">
                            <h2 className="secondaryHeading">
                                Reject <span>{selectedRequests.length}</span> {showRejectModel === "Refund" ? "refund" : "withdrawal"} request{selectedRequests.length === 1 ? '' : 's'}
                            </h2>
                            <div className="horizontalLine"></div>
                            <p className="rejectModalNote">
                                {showRejectModel === "Refund"
                                    ? "The credits debited when the buyer made this request will be returned to their account balance."
                                    : "The amount being held for this request goes back to the seller's available balance, so they can correct the problem and request again."}
                                {' '}They will be emailed the reason you give below.
                            </p>
                            <div className="inputDiv">
                                <label>Reason <span>*</span></label>
                                <textarea
                                    value={rejectReason}
                                    onChange={(e) => setRejectReason(e.target.value)}
                                    placeholder="e.g. The PayPal address on your account is not valid — please update it and request again."
                                    className='inputField'
                                    rows={3}
                                />
                            </div>
                        </div>

                        <div className="buttonsDiv" style={{ marginTop: "20px" }}>
                            <button className="secondaryBtn" onClick={() => { setShowRejectModel(null); setRejectReason(''); }}>Cancel</button>
                            <button className="primaryBtn" onClick={rejectRequests} disabled={!rejectReason.trim()}>Reject Request{selectedRequests.length === 1 ? '' : 's'}</button>
                        </div>
                    </div>

                    <div className="popupCloseBtn">
                        <IoIosCloseCircleOutline className="icon" onClick={() => { setShowRejectModel(null); setRejectReason(''); }} />
                    </div>
                </div>
            )}

            {showReleaseModel && (() => {
                const totals = selectedTotals();
                const method = value === 4 || value === 5 ? "Wise" : value === 6 || value === 7 ? "Paypal" : "Stripe";

                return (
                <div className="popupDiv">
                    <div className="popupContent releasePopupContent">
                        <div className="form">
                            <h2 className="secondaryHeading">Confirm <span>{showReleaseModel === "Release" ? "Releasing" : "Refunding"}</span> Payments - <span>{method}</span></h2>
                            <div className="horizontalLine"></div>
                            <p>You are about to release a total of <strong>${totals.net.toFixed(2)}</strong> to the {showReleaseModel === "Release" ? "seller(s)" : "buyer(s)"}. Please ensure you have sufficient funds in your linked {method} account!</p>
                            {totals.fee > 0 && (
                                <p>This is ${totals.gross.toFixed(2)} requested less ${totals.fee.toFixed(2)} in payout fees charged to the seller(s).</p>
                            )}
                        </div>

                        <div className="buttonsDiv" style={{ marginTop: "20px" }}>
                            <button className="secondaryBtn" onClick={() => setShowReleaseModel(null)}>Cancel</button>
                            <button className="primaryBtn" onClick={showReleaseModel === "Release" ? releasePayments : refundPayments} disabled={releaseLoading}>{showReleaseModel === "Release" ? "Release" : "Refund"} ${totals.net.toFixed(2)}</button>
                        </div>
                    </div>

                    <div className="popupCloseBtn">
                        <IoIosCloseCircleOutline className="icon" onClick={() => setShowReleaseModel(null)} />
                    </div>
                </div>
                );
            })()}


        </div>
    );
};

export default AdminPayments;




function PendingPayments({ requests, selectedRequests, handleSelectRequest, handleSelectAll, setShowMarkPaidModel, setShowReleaseModel, setShowRejectModel, paymentMethod }) {

    const areAllSelected = selectedRequests.length === requests.length;
    const [showSellerDetailsModel, setShowSellerDetailsModel] = useState(false);
    const [history, setHistory] = useState([]);
    const [earnings, setEarnings] = useState({
        availableBalance: 0,
        totalEarnings: 0,
        paidBalance: 0,
        requestedForWithdrawal: 0,
        productsSold: 0,
    });
    const [messageToRecipient, setMessageToRecipient] = useState("");
    const [showMessageToRecipientModel, setShowMessageToRecipientModel] = useState(false);

    const token = localStorage.getItem("adminToken");

    const showSellerDetails = (sellerId) => {

        setShowSellerDetailsModel(true);

        axios.get(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/payments/seller/${sellerId}`, {
            headers: { Authorization: `Admin ${token}` }
        })
            .then(response => {
                if (response.data.success)
                    setHistory(response.data.history);
            })
            .catch(e => {
                showToast("Something went wrong with payments", 'error');
            });

        axios.get(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/payments/seller/${sellerId}/earnings`, {
            headers: { Authorization: `Admin ${token}` }
        })
            .then(response => {
                if (response.data.success)
                    setEarnings(response.data.earnings);
            })
            .catch(e => {
                showToast("Something went wrong with earnings", 'error');
            });
    };

    const paymentElems = requests.length > 0 ? requests.map((request, index) => (
        <div key={index}>
            <div className="requestRow row">
                <div><input
                    type="checkbox"
                    checked={selectedRequests.includes(request._id)}
                    onChange={() => handleSelectRequest(request._id)}
                />
                </div>
                <p className="dateField field">{formatDate(request?.createdAt)}</p>
                <Link to={`/vendorDetail/${request?.userId?.sellerId?._id}`} className="userField field">{request?.userId?.username + " >"}</Link>
                <p className="priceField field">${request?.amount.toFixed(2)}</p>
                <div className="actionsField field">
                    <FaEye className='icon' onClick={() => showSellerDetails(request?.userId?.sellerId?._id)} />
                </div>
            </div>
            {requests.length > 1 && requests.length - 1 !== index && <div className="horizontalLine"></div>}
        </div>
    ))
        : <div className="row">Nothing to show here...</div>;

    const historyElems = history.length > 0 ? history.map((item, index) => (
        <div key={index}>
            <div className="requestRow row">
                <div className="titleField field">
                    <p className="title">{item?.description}</p>
                </div>
                <p className="dateField field">{formatDate(item?.date)}</p>
                <p className="buyerField field">{item?.buyerUsername || "-"}</p>
                <p className="typeField field">{item?.itemType || "-"}</p>
                <p className="priceField field">${item?.amount.toFixed(2)}</p>
            </div>
            {history.length > 1 && history.length - 1 !== index && <div className="horizontalLine"></div>}
        </div>
    ))
        : <div className="row">Nothing to show here...</div>;

    // Seller ID must be the seller profile id here too, so this export reconciles
    // against the completed-payments export.
    const csvData = requests.map(request => ({
        "Requested On": formatDate(request?.createdAt),
        "Seller": request?.userId?.username,
        "Seller ID": request?.userId?.sellerId?._id,
        "Requested Amount": request?.amount.toFixed(2),
        "Payout Fee": (request?.payoutFee || 0).toFixed(2),
        "Net Payout": (request?.payableAmount ?? request?.amount).toFixed(2)
    }));

    const headers = [
        { label: "Requested On", key: "Requested On" },
        { label: "Seller", key: "Seller" },
        { label: "Seller ID", key: "Seller ID" },
        { label: "Requested Amount", key: "Requested Amount" },
        { label: "Payout Fee", key: "Payout Fee" },
        { label: "Net Payout", key: "Net Payout" }
    ];

    // PayPal debits the sender for this amount plus its own fee, so sending the
    // net here is what passes the payout fee on to the seller.
    const csvDataPaypal = requests.map(request => ({
        "Email": request?.userId?.sellerId?.paypalEmail,
        "Amount": (request?.payableAmount ?? request?.amount).toFixed(2),
        "Currency code": 'USD',
        "Reference ID (optional)": request?._id,
        "Message to recipient": messageToRecipient,
        "Recipient wallet": 'PayPal',
    }));

    const headersPaypal = [
        { label: "Email", key: "Email" },
        { label: "Amount", key: "Amount" },
        { label: "Currency code", key: "Currency code" },
        { label: "Reference ID (optional)", key: "Reference ID (optional)" },
        { label: "Message to recipient", key: "Message to recipient" },
        { label: "Recipient wallet", key: "Recipient wallet" }
    ];

    return (
        <div className="pendingPaymentsDiv tableDiv">
            <div className="tableContent">
                <div className="upper">
                    <h2 className="secondaryHeading"><span>Pending {paymentMethod === "Stripe" ? "Stripe" : paymentMethod === "Wise" ? "Wise" : "Paypal"}</span> Withdrawal requests from Sellers</h2>
                    <div className="upperRight">
                        {paymentMethod !== "Paypal" && <CSVLink data={csvData} headers={headers} filename={`${paymentMethod === "Stripe" ? "pending_stripe_payments" : "pending_wise_payments"}.csv`} className="secondaryBtn">Export CSV</CSVLink>}
                        {paymentMethod === "Paypal" && <button className="secondaryBtn" onClick={() => setShowMessageToRecipientModel(true)}>Export CSV</button>}
                        <button className="secondaryBtn" disabled={selectedRequests.length < 1} onClick={() => setShowMarkPaidModel("Release")}>Mark Paid</button>
                        {/* Declining returns the held balance to the seller so they
                            can correct their details and request again. */}
                        <button className="secondaryBtn rejectBtn" disabled={selectedRequests.length < 1} onClick={() => setShowRejectModel("Payment")}>Reject</button>
                        {/* Release Payment sends a Stripe transfer, so it must never be
                            offered for Wise or Paypal requests. */}
                        {paymentMethod === "Stripe" && <button className="secondaryBtn" disabled={selectedRequests.length < 1} onClick={() => setShowReleaseModel("Release")}>Release Payment</button>}
                    </div>
                </div>
                <div className="header">
                    {requests.length > 0 && <div><input
                        type="checkbox"
                        checked={areAllSelected}
                        onChange={() => handleSelectAll(requests, areAllSelected)}
                    />
                    </div>}
                    <p>Date</p>
                    <p>Seller</p>
                    <p>Amount</p>
                    <p>Actions</p>
                </div>
                <div className="rows">{paymentElems}</div>
            </div>

            {showSellerDetailsModel && (
                <div className="popupDiv">

                    <div className="popupContent">

                        <div className='sellerDetails form'>

                            <h2 className="secondaryHeading"><span>Seller</span> Details</h2>
                            <div className="horizontalLine"></div>

                            <div className="sellerEarnings form rowsParent">
                                <h4 className='fw600'>Earnings</h4>
                                <div className="row">
                                    <p>Total Earnings</p>
                                    <div className="fw600">${earnings.totalEarnings.toFixed(2)}</div>
                                </div>
                                <div className="row">
                                    <p>Available Balance</p>
                                    <div className="fw600">${earnings.availableBalance.toFixed(2)}</div>
                                </div>
                                <div className="row">
                                    <p>Paid Balance</p>
                                    <div className="fw600">${earnings.paidBalance.toFixed(2)}</div>
                                </div>
                                <div className="row">
                                    <p>Requested for Withdrawal</p>
                                    <div className="fw600">${earnings.requestedForWithdrawal.toFixed(2)}</div>
                                </div>
                                <div className="row">
                                    <p>Products Sold</p>
                                    <div className="fw600">{earnings.productsSold}</div>
                                </div>
                            </div>

                            <div className="horizontalLine"></div>

                            <div className="sellerHistory tableDiv">
                                <h4 className='fw600'>History</h4>
                                <div className="tableContent">
                                    <div className="header">
                                        <p className="title">Activity</p>
                                        <p>Date</p>
                                        <p>Buyer</p>
                                        <p>Item Type</p>
                                        <p>Amount</p>
                                    </div>
                                    <div className="rows">{historyElems}</div>
                                </div>
                            </div>

                        </div>

                        <div className="buttonsDiv" style={{ marginTop: "20px" }}>
                            {/* <button type="submit" className='primaryBtn' onClick={handleSubmit}>Save Stripe Details</button> */}
                            <button className="secondaryBtn" type="button" onClick={() => setShowSellerDetailsModel(false)}>Close</button>
                        </div>

                    </div>

                    <div className="popupCloseBtn">
                        <IoIosCloseCircleOutline className="icon" onClick={() => setShowSellerDetailsModel(false)} />
                    </div>

                </div>
            )}

            {showMessageToRecipientModel && (
                <div className="popupDiv">

                    <div className="popupContent">

                        <div className="form">
                            <div className="inputDiv">
                                <label>Message For Recipient</label>
                                <textarea value={messageToRecipient} onChange={(e) => setMessageToRecipient(e.target.value)} className='inputField' placeholder='Enter Message for Recipient' />
                            </div>
                        </div>

                        <div className="buttonsDiv" style={{ marginTop: "20px" }}>
                            <CSVLink data={csvDataPaypal} headers={headersPaypal} filename="pending_paypal_payments.csv" className="secondaryBtn" onClick={() => setShowMessageToRecipientModel(false)}>Generate CSV</CSVLink>
                            <button className="secondaryBtn" type="button" onClick={() => setShowMessageToRecipientModel(false)}>Close</button>
                        </div>

                    </div>

                    <div className="popupCloseBtn">
                        <IoIosCloseCircleOutline className="icon" onClick={() => setShowMessageToRecipientModel(false)} />
                    </div>

                </div>
            )}
        </div>
    )
}


function PendingRefunds({ requests, selectedRequests, handleSelectRequest, handleSelectAll, setShowMarkPaidModel, setShowReleaseModel, setShowRejectModel }) {

    const areAllSelected = selectedRequests.length === requests.length;

    // const token = localStorage.getItem("adminToken");

    const paymentElems = requests.length > 0 ? requests.map((request, index) => (
        <div key={index}>
            <div className="requestRow row">
                <div><input
                    type="checkbox"
                    checked={selectedRequests.includes(request._id)}
                    onChange={() => handleSelectRequest(request._id)}
                />
                </div>
                <p className="dateField field">{formatDate(request?.createdAt)}</p>
                <p className="userField field">{request?.userId?.username}</p>
                <p className="priceField field">${request?.amount.toFixed(2)}</p>
                {/* <div className="actionsField field">
                    <FaEye className='icon' onClick={() => showSellerDetails(request?.userId?.sellerId)} />
                </div> */}
            </div>
            {requests.length > 1 && requests.length - 1 !== index && <div className="horizontalLine"></div>}
        </div>
    ))
        : <div className="row">Nothing to show here...</div>;

    const csvData = requests.map(request => ({
        "Date": formatDate(request?.createdAt),
        "Buyer": request?.userId?.username,
        "Buyer ID": request?.userId?._id,
        "Amount": request?.amount.toFixed(2),
    }));

    const headers = [
        { label: "Date", key: "Date" },
        { label: "Buyer", key: "Buyer" },
        { label: "Buyer ID", key: "Buyer ID" },
        { label: "Amount", key: "Amount" }
    ];

    return (
        <div className="pendingPaymentsDiv tableDiv">
            <div className="tableContent">
                <div className="upper">
                    <h2 className="secondaryHeading"><span>Pending</span> Refunds to Buyers</h2>
                    <div className="upperRight">
                        <CSVLink data={csvData} headers={headers} filename={"pending_refunds.csv"} className="secondaryBtn">Export CSV</CSVLink>
                        <button className="secondaryBtn" disabled={selectedRequests.length < 1} onClick={() => setShowMarkPaidModel("Refund")}>Mark Paid</button>
                        {/* Declining returns the credits that were debited when the
                            buyer made the request. */}
                        <button className="secondaryBtn rejectBtn" disabled={selectedRequests.length < 1} onClick={() => setShowRejectModel("Refund")}>Reject</button>
                        <button className="secondaryBtn" disabled={selectedRequests.length < 1} onClick={() => setShowReleaseModel("Refund")}>Refund Payment</button>
                    </div>
                </div>
                <div className="header">
                    {requests.length > 0 && <div><input
                        type="checkbox"
                        checked={areAllSelected}
                        onChange={() => handleSelectAll(requests, areAllSelected)}
                    />
                    </div>}
                    <p>Date</p>
                    <p>Buyer</p>
                    <p>Amount</p>
                    {/* <p>Actions</p> */}
                </div>
                <div className="rows">{paymentElems}</div>
            </div>
        </div>
    )
}



function CompletedPayments({ requests, selectedRequests, handleSelectRequest, handleSelectAll, moveToPending, paymentMethod }) {

    const [showPaymentDetailsModel, setShowPaymentDetailsModel] = useState(null);
    const areAllSelected = selectedRequests.length === requests.length;

    const paymentElems = requests.length > 0 ? requests.map((request, index) => (
        <div key={index}>
            <div className="requestRow row">
                <div><input
                    type="checkbox"
                    checked={selectedRequests.includes(request._id)}
                    onChange={() => handleSelectRequest(request._id)}
                />
                </div>
                <p className="dateField field">{formatDate(request?.createdAt)}</p>
                <p className="dateField field">{formatDate(request?.paidOn)}</p>
                <Link to={`/chwi-admin/sellers/${request?.userId?.sellerId?._id}`} className="userField field">{request?.userId?.username + " >"}</Link>
                <p className="typeField field">{request?.paymentType}</p>
                <p className="priceField field">${request?.amount.toFixed(2)}</p>
                <div className="actionsField field">
                    <FaEye className='icon' onClick={() => setShowPaymentDetailsModel(request)} />
                </div>
            </div>
            {requests.length > 1 && requests.length - 1 !== index && <div className="horizontalLine"></div>}
        </div>
    ))
        : <div className="row">Nothing to show here...</div>;

    const csvData = requests.map(request => ({
        "Requested On": formatDate(request?.createdAt),
        "Paid On": formatDate(request?.paidOn),
        "Seller": request?.userId?.username,
        "Seller ID": request?.userId?.sellerId?._id,
        "Amount": request?.amount.toFixed(2),
        "Payment Approach": request?.paymentType,
        "Comment": request?.comment || "-",
    }));

    const headers = [
        { label: "Requested On", key: "Requested On" },
        { label: "Paid On", key: "Paid On" },
        { label: "Seller", key: "Seller" },
        { label: "Seller ID", key: "Seller ID" },
        { label: "Amount", key: "Amount" },
        { label: "Payment Approach", key: "Payment Approach" },
        { label: "Comment", key: "Comment" },
    ];

    return (
        <div className="tableDiv">
            <div className="tableContent">
                <div className="upper">
                    <h2 className="secondaryHeading"><span>Completed {paymentMethod === "Stripe" ? "Stripe" : paymentMethod === "Wise" ? "Wise" : "Paypal"}</span> Withdrawal requests</h2>
                    <div className="upperRight">
                        <CSVLink data={csvData} headers={headers} filename={`${paymentMethod === "Stripe" ? "completed_stripe_payments" : paymentMethod === "Wise" ? "completed_wise_payments" : "completed_paypal_payments"}.csv`} className="secondaryBtn">Export CSV</CSVLink>
                        <button className="secondaryBtn" disabled={selectedRequests.length < 1} onClick={() => moveToPending("Payment")}>Move to Pending</button>
                    </div>
                </div>
                <div className="header">
                    {requests.length > 0 && <div><input
                        type="checkbox"
                        checked={areAllSelected}
                        onChange={() => handleSelectAll(requests, areAllSelected)}
                    />
                    </div>}
                    <p>Requested On</p>
                    <p>Paid On</p>
                    <p>Seller</p>
                    <p>Payed By</p>
                    <p>Amount</p>
                    <p>Actions</p>
                </div>
                <div className="rows">{paymentElems}</div>
            </div>
            {showPaymentDetailsModel && (
                <div className="popupDiv">

                    <div className="popupContent">

                        <div className='sellerDetails form'>

                            <h2 className="secondaryHeading"><span>Payment</span> Details</h2>
                            <div className="horizontalLine"></div>

                            <div className="rowsParent">
                                <div className="row">
                                    <p>Request Made On</p>
                                    <div className="fw600">{formatDate(showPaymentDetailsModel?.createdAt)}</div>
                                </div>
                                <div className="row">
                                    <p>Payment Paid On</p>
                                    <div className="fw600">{formatDate(showPaymentDetailsModel?.paidOn)}</div>
                                </div>
                                <div className="row">
                                    <p>Requested By</p>
                                    <div className="fw600">{showPaymentDetailsModel?.userId?.username}</div>
                                </div>
                                <div className="row">
                                    <p>Amount</p>
                                    <div className="fw600">${showPaymentDetailsModel?.amount.toFixed(2)}</div>
                                </div>
                                <div className="row">
                                    <p>Payment Approach</p>
                                    <div className="fw600">{showPaymentDetailsModel?.paymentType}</div>
                                </div>
                                {showPaymentDetailsModel?.paymentType === "Manual" && <div className="row">
                                    <p>Comment</p>
                                    <div className="fw600">{showPaymentDetailsModel?.comment}</div>
                                </div>}
                            </div>

                        </div>

                        <div className="buttonsDiv" style={{ marginTop: "20px" }}>
                            <button className="secondaryBtn" type="button" onClick={() => setShowPaymentDetailsModel(null)}>Close</button>
                        </div>

                    </div>

                    <div className="popupCloseBtn">
                        <IoIosCloseCircleOutline className="icon" onClick={() => setShowPaymentDetailsModel(null)} />
                    </div>

                </div>
            )}
        </div>
    )
}



function CompletedRefunds({ requests, selectedRequests, handleSelectRequest, handleSelectAll, moveToPending }) {

    const [showPaymentDetailsModel, setShowPaymentDetailsModel] = useState(null);
    const areAllSelected = selectedRequests.length === requests.length;

    const paymentElems = requests.length > 0 ? requests.map((request, index) => (
        <div key={index}>
            <div className="requestRow row">
                <div><input
                    type="checkbox"
                    checked={selectedRequests.includes(request._id)}
                    onChange={() => handleSelectRequest(request._id)}
                />
                </div>
                <p className="dateField field">{formatDate(request?.createdAt)}</p>
                <p className="dateField field">{formatDate(request?.paidOn)}</p>
                <p className="userField field">{request?.userId?.username}</p>
                <p className="typeField field">{request?.paymentType}</p>
                <p className="priceField field">${request?.amount.toFixed(2)}</p>
                <div className="actionsField field">
                    <FaEye className='icon' onClick={() => setShowPaymentDetailsModel(request)} />
                </div>
            </div>
            {requests.length > 1 && requests.length - 1 !== index && <div className="horizontalLine"></div>}
        </div>
    ))
        : <div className="row">Nothing to show here...</div>;

    const csvData = requests.map(request => ({
        "Order Cancellation Date": formatDate(request?.createdAt),
        "Paid On": formatDate(request?.paidOn),
        "Buyer": request?.userId?.username,
        "Buyer ID": request?.userId?._id,
        "Amount": request?.amount.toFixed(2),
        "Payment Approach": request?.paymentType,
        "Comment": request?.comment || "-",
    }));

    const headers = [
        { label: "Order Cancellation Date", key: "Order Cancellation Date" },
        { label: "Paid On", key: "Paid On" },
        { label: "Buyer", key: "Buyer" },
        { label: "Buyer ID", key: "Buyer ID" },
        { label: "Amount", key: "Amount" },
        { label: "Payment Approach", key: "Payment Approach" },
        { label: "Comment", key: "Comment" }
    ];

    return (
        <div className="tableDiv">
            <div className="tableContent">
                <div className="upper">
                    <h2 className="secondaryHeading"><span>Paid</span> Refunds</h2>
                    <div className="upperRight">
                        <CSVLink data={csvData} headers={headers} filename={"completed_refunds.csv"} className="secondaryBtn">Export CSV</CSVLink>
                        <button className="secondaryBtn" disabled={selectedRequests.length < 1} onClick={() => moveToPending("Refund")}>Move to Pending</button>
                        {/* <button className="secondaryBtn" disabled={selectedRequests.length < 1}>Release Payment</button> */}
                    </div>
                </div>
                <div className="header">
                    {requests.length > 0 && <div><input
                        type="checkbox"
                        checked={areAllSelected}
                        onChange={() => handleSelectAll(requests, areAllSelected)}
                    />
                    </div>}
                    <p>Date</p>
                    <p>Paid On</p>
                    <p>Buyer</p>
                    <p>Payed By</p>
                    <p>Amount</p>
                    <p>Actions</p>
                </div>
                <div className="rows">{paymentElems}</div>
            </div>
            {showPaymentDetailsModel && (
                <div className="popupDiv">

                    <div className="popupContent">

                        <div className='sellerDetails form'>

                            <h2 className="secondaryHeading"><span>Payment</span> Details</h2>
                            <div className="horizontalLine"></div>

                            <div className="rowsParent">
                                <div className="row">
                                    <p>Order Cancelled On</p>
                                    <div className="fw600">{formatDate(showPaymentDetailsModel?.createdAt)}</div>
                                </div>
                                <div className="row">
                                    <p>Refund Paid On</p>
                                    <div className="fw600">{formatDate(showPaymentDetailsModel?.paidOn)}</div>
                                </div>
                                <div className="row">
                                    <p>Buyer</p>
                                    <div className="fw600">{showPaymentDetailsModel?.userId?.username}</div>
                                </div>
                                <div className="row">
                                    <p>Amount</p>
                                    <div className="fw600">${showPaymentDetailsModel?.amount.toFixed(2)}</div>
                                </div>
                                <div className="row">
                                    <p>Payment Approach</p>
                                    <div className="fw600">{showPaymentDetailsModel?.paymentType}</div>
                                </div>
                                {showPaymentDetailsModel?.paymentType === "Manual" && <div className="row">
                                    <p>Comment</p>
                                    <div className="fw600">{showPaymentDetailsModel?.comment}</div>
                                </div>}
                            </div>

                        </div>

                        <div className="buttonsDiv" style={{ marginTop: "20px" }}>
                            <button className="secondaryBtn" type="button" onClick={() => setShowPaymentDetailsModel(null)}>Close</button>
                        </div>

                    </div>

                    <div className="popupCloseBtn">
                        <IoIosCloseCircleOutline className="icon" onClick={() => setShowPaymentDetailsModel(null)} />
                    </div>

                </div>
            )}
        </div>
    )
}



function CustomTabPanel(props) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
        </div>
    );
}

CustomTabPanel.propTypes = {
    children: PropTypes.node,
    index: PropTypes.number.isRequired,
    value: PropTypes.number.isRequired,
};

function a11yProps(index) {
    return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`,
    };
}